import { useState, type ReactNode } from 'react';
import { classNames } from '~/utils/classNames';
import type { Tone } from './payments-model';

// Presentational primitives shared by the Payments panel sections. Styling
// follows DomainsPanel (raised hairline cards, caps captions, lime used once).

export const CARD =
  'rounded-xl border border-hairline bg-raised ' +
  'shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset,0_12px_28px_-22px_rgba(0,0,0,0.85)]';

export const CAPTION = 'text-[10px] font-medium uppercase tracking-[0.14em] text-muted';

/** Quiet bordered button (secondary actions); the lime `ui-btn-accent` is reserved for Go live. */
export const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-subtle bg-surface px-3 py-1.5 text-[12px] font-medium text-primary transition-all duration-150 hover:border-prominent hover:bg-sunken active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 touch:min-h-10';

export const BTN_GHOST =
  'inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-muted transition-colors duration-150 hover:bg-white/[0.04] hover:text-primary disabled:pointer-events-none disabled:opacity-50 touch:min-h-10';

const TONES: Record<Tone, string> = {
  accent: 'border-accent/25 bg-accent/10 text-accent',
  success: 'border-success/25 bg-success/10 text-success',
  warning: 'border-warning/25 bg-warning/10 text-warning',
  danger: 'border-danger/25 bg-danger/10 text-danger',
  info: 'border-info/25 bg-info/10 text-info',
  muted: 'border-subtle bg-white/[0.03] text-muted',
};

const DOTS: Record<Tone, string> = {
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  muted: 'bg-faint',
};

export function Pill({ tone, label, pulse }: { tone: Tone; label: string; pulse?: boolean }) {
  return (
    <span
      className={classNames(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em]',
        TONES[tone],
      )}
    >
      <span className="relative flex h-1.5 w-1.5">
        {pulse && (
          <span
            className={classNames(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
              DOTS[tone],
            )}
          />
        )}
        <span className={classNames('relative inline-flex h-1.5 w-1.5 rounded-full', DOTS[tone])} />
      </span>
      {label}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <div className={classNames('i-svg-spinners:90-ring-with-bg', className ?? 'text-[13px]')} />;
}

export function Section({
  title,
  icon,
  aside,
  children,
}: {
  title: string;
  icon: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={classNames(icon, 'text-[13px] text-muted')} />
          <h4 className={CAPTION}>{title}</h4>
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  return (
    <div className={classNames(CARD, 'space-y-2.5 p-4')} aria-hidden>
      <div className="h-3 w-1/3 animate-pulse rounded bg-sunken" />
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className={classNames('h-2.5 animate-pulse rounded bg-sunken', i % 2 ? 'w-1/2' : 'w-4/5')} />
      ))}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="ui-banner-error items-start leading-relaxed">
      <div className="i-ph:warning-circle mt-px shrink-0 text-sm text-danger" />
      <span className="text-[12px] text-danger/90">{message}</span>
    </div>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className={classNames(CARD, 'flex items-center justify-between gap-3 px-4 py-3')}>
      <div className="flex min-w-0 items-center gap-2">
        <div className="i-ph:warning-circle shrink-0 text-sm text-danger" />
        <span className="text-[12px] text-secondary">{message}</span>
      </div>
      <button onClick={onRetry} className={BTN_SECONDARY}>
        <div className="i-ph:arrow-clockwise text-[13px]" />
        Retry
      </button>
    </div>
  );
}

export function CopyField({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-hairline bg-sunken">
      {label && <div className={classNames(CAPTION, 'border-b border-hairline px-3 py-1.5')}>{label}</div>}
      <div className="flex items-center gap-2 py-1.5 pl-3 pr-1.5">
        <code className="min-w-0 flex-1 break-all font-geist-mono text-[11px] text-primary">{value}</code>
        <button
          onClick={copy}
          className="ui-btn-icon shrink-0"
          title="Copy to clipboard"
          aria-label="Copy to clipboard"
        >
          <div className={classNames(copied ? 'i-ph:check text-accent' : 'i-ph:copy text-muted', 'text-[13px]')} />
        </button>
      </div>
    </div>
  );
}

/** Small pill switcher (Test/Live, sub-views). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="ui-segmented max-w-full overflow-x-auto" role="tablist" aria-label={label}>
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={value === v}
          onClick={() => onChange(v)}
          className={classNames(
            'ui-segmented-btn shrink-0 whitespace-nowrap !px-3 !py-1 !text-[11px]',
            value === v && 'ui-segmented-btn-active',
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Native select styled like `ui-input`, with a caret. */
export function Select({
  value,
  onChange,
  children,
  label,
  disabled,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  children: ReactNode;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={classNames('relative', className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        disabled={disabled}
        className="ui-input appearance-none !py-2 pr-7 disabled:opacity-50"
      >
        {children}
      </select>
      <div className="i-ph:caret-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-muted" />
    </div>
  );
}

/** On/off switch with an inline label. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className={classNames('inline-flex items-center gap-2', disabled && 'opacity-50')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={classNames('ui-switch shrink-0', checked && 'ui-switch-on', disabled && 'cursor-not-allowed')}
      >
        <span className={classNames('ui-switch-thumb', checked && 'ui-switch-thumb-on')} />
      </button>
      <span className="text-[12px] text-secondary">{label}</span>
    </label>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[11px] text-secondary">{label}</span>
      {children}
      {hint && <span className="block text-[11px] leading-relaxed text-muted">{hint}</span>}
    </label>
  );
}
